import { Drawer } from 'components/Drawer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { observer } from 'mobx-react-lite'
import Styles from './CollaborationDrawer.module.scss'
import { CollaborationList } from './CollaborationList'
import { useDocStore } from 'stores/DocStore'
import { PreviousItemButton } from './PreviousItemButton'
import { NextItemButton } from './NextItemButton'
import { useState } from 'react'
import { Button } from 'blaze-components/WriterTopBar/Button'
import { useRootStore } from 'stores/RootStore'
import { useLayout } from 'stores/LayoutStore'
import { Comment } from 'types'
import { Comment as CommentItem } from './Comment'
import { Button as BlazeButton } from 'blaze-ui/Button'
import { CommentInput } from 'common/Prose/Toolbar/MobileWriterFormatBar/CommentInput'
import { EditorView } from 'prosemirror-view'
import { CollabItemsFilters } from 'components/DocRightRailV2/CollabItemsFilters/CollabItemsFilters'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CollabItemType } from 'stores/DocStore.types'
import { DrawerProps } from 'components/Drawer/Root'

const SelectedItemHeading = ({ index, count }: { index: number; count: number }) => (
  <div className={Styles.heading}>
    <PreviousItemButton />
    <Drawer.Title asChild>
      <Heading level={5}>{`${index} of ${count}`}</Heading>
    </Drawer.Title>
    <NextItemButton />
  </div>
)

interface Props extends DrawerProps {
  view?: EditorView
}

export const CollaborationDrawer = observer(({ view, ...props }: Props) => {
  const docStore = useDocStore()
  const { collabItemsStore, orderedFilteredCollabItems } = docStore
  const selectedItemIndex = orderedFilteredCollabItems.findIndex(
    (item) => (item.item as Comment).id === collabItemsStore.selectedId,
  )
  const selectedItem = orderedFilteredCollabItems[selectedItemIndex]
  const layout = useLayout()
  const layoutViewport = layout.viewport
  const { comments: commentsStore } = useRootStore()
  const [footer, setFooter] = useState<HTMLDivElement | null>(null)
  const [showNewComment, setShowNewComment] = useState(false)
  const fullHeight = !selectedItem
  const currentWorkspaceId = useCurrentWorkspaceId()

  const onClose = () => {
    setShowNewComment(false)
    commentsStore.select(undefined)
    collabItemsStore.selectById(undefined)
  }

  return (
    <Drawer.Root {...props} onClose={onClose}>
      <Drawer.Portal>
        <Drawer.Content fullHeight={fullHeight} heightDynamic={!!selectedItem} layoutViewport={layoutViewport}>
          <Drawer.InnerContent isSnappedTop={fullHeight} color="var(--light-100)" withShadow={!!selectedItem}>
            <Drawer.Top>
              <Drawer.Close />
              {selectedItem && (
                <SelectedItemHeading index={selectedItemIndex + 1} count={orderedFilteredCollabItems.length} />
              )}
              {!selectedItem && <Heading level={5}>Comments</Heading>}

              {selectedItem ? (
                <div className={Styles.seeAll}>
                  <Button
                    onPress={() => {
                      // There's an issue when expanding the drawer to full height from here, where
                      // a comment would remain selected somehow. Solved it by adding a small timeout.
                      setTimeout(() => {
                        commentsStore.select(undefined)
                        docStore.collabItemsStore.selectById(undefined)
                      }, 100)
                    }}
                  >
                    See All
                  </Button>
                </div>
              ) : (
                <div className={Styles.seeAll}>
                  <CollabItemsFilters workspaceId={currentWorkspaceId} />
                </div>
              )}
            </Drawer.Top>
            <Drawer.Container padding={selectedItem ? '10px' : '15px'}>
              {!selectedItem && <CollaborationList />}
              {selectedItem && selectedItem.type === CollabItemType.Comment && (
                <CommentItem comment={selectedItem.item} replyFormContainer={footer} view={view} />
              )}
            </Drawer.Container>
            <div className={Styles.footer} ref={(node) => setFooter(node)} />
          </Drawer.InnerContent>
          {!selectedItem && (
            <div className={Styles.createCommentButton}>
              {showNewComment ? (
                <CommentInput view={view} onSuccess={() => setShowNewComment(false)} />
              ) : (
                <BlazeButton
                  fullWidth
                  size="lg"
                  onPress={() => {
                    setShowNewComment(true)
                  }}
                >
                  Create a comment
                </BlazeButton>
              )}
            </div>
          )}
        </Drawer.Content>
        {!selectedItem && <Drawer.Overlay />}
      </Drawer.Portal>
    </Drawer.Root>
  )
})
